/**
 * Pricing-only optimistic-concurrency helpers for the writer-checked tables
 * (BROKERS, PRICING_FREQUENCY, PRICING, MOM_PRICING_NOTES).
 *
 * MODIFIED_DATE is the concurrency token. The generic writer helpers
 * (loadedModifiedDate, withLoadedModifiedDate, isVersionConflict,
 * getWriteErrorMessage and the messages) come from @1823-partners/core; these
 * are the pieces specific to how the pricing grids keep unsaved edits across a
 * reload. Never retry a 409 with a refreshed MODIFIED_DATE: that is a blind
 * overwrite.
 */
import { MODIFIED_DATE_FIELD, loadedModifiedDate } from '@1823-partners/core';

/**
 * Client-only row field (`_`-prefixed, so every payload strips it): for each
 * field the server sent as null or '', { raw, shown } = the value as sent and
 * the value the grid displays. It lives and moves with the row's loaded
 * MODIFIED_DATE token, so the raw blanks always belong to the version the
 * token names. Pricing grids use it to send untouched blanks back unchanged.
 */
export const BLANK_BASELINE_FIELD = '_BLANK_BASELINE';

const withBaselineOf = (row, source) =>
  source && source[BLANK_BASELINE_FIELD] ? { ...row, [BLANK_BASELINE_FIELD]: source[BLANK_BASELINE_FIELD] } : row;

/**
 * `shownRow` (the displayed row built from `rawRow`) with the blank baseline
 * of the fields `rawRow` sent as null or ''. MODIFIED_DATE is the token, not
 * a business field, and is left to the date handling.
 */
export const withBlankBaseline = (rawRow, shownRow) => {
  const baseline = {};
  Object.keys(rawRow).forEach((field) => {
    if (field === MODIFIED_DATE_FIELD) return;
    if (rawRow[field] === null || rawRow[field] === '') {
      baseline[field] = { raw: rawRow[field], shown: shownRow[field] };
    }
  });
  return { ...shownRow, [BLANK_BASELINE_FIELD]: baseline };
};

/**
 * `row` ready to save with the loaded blanks it still shows sent back as
 * loaded (null stays null). Only a persisted row carrying its loaded
 * MODIFIED_DATE qualifies: a new, duplicated or newly keyed row has no loaded
 * version and keeps the grid's own defaults. A field the user edited
 * (`edits`, the row's updatedRows entry) is explicit and is never restored,
 * so clearing a value or setting a nullable boolean to false is sent as is.
 */
export const restoreBlankValues = (row, edits = {}) => {
  const baseline = row[BLANK_BASELINE_FIELD];
  if (!baseline || loadedModifiedDate(row) === null) return row;
  const restored = { ...row };
  Object.keys(baseline).forEach((field) => {
    if (!(field in edits) && row[field] === baseline[field].shown) {
      restored[field] = baseline[field].raw;
    }
  });
  return restored;
};

/**
 * A refreshed row that has unsaved local edits keeps the MODIFIED_DATE those
 * edits were based on, so a background refresh cannot quietly turn them into an
 * overwrite of someone else's save. Its blank baseline stays with that token.
 */
export const keepBaseModifiedDate = (freshRow, baseRow) =>
  baseRow ? withBaselineOf({ ...freshRow, [MODIFIED_DATE_FIELD]: loadedModifiedDate(baseRow) }, baseRow) : freshRow;

/** An edited key names a different row, which the server has not seen: null token. */
export const asNewKey = (row) => ({ ...row, [MODIFIED_DATE_FIELD]: null });

/**
 * The MODIFIED_DATE a persisted row carries after an edit to one of its
 * `keyFields`. Another key names a different row: null. Editing the key back
 * to the one the row had before its first key edit restores the MODIFIED_DATE
 * it had there, so a rename undone by hand or undo is not sent as a new row.
 *
 * `loadedKeys` (tracking key -> { key, modifiedDate }) remembers that key and
 * token while the row is under another key; it is updated in place, so the
 * caller keeps it in a ref (it must be current for the next edit in the same
 * tick, e.g. a paste). The caller clears it whenever it clears updatedRows.
 * `rowBefore` is the row with its key before this edit, `rowAfter` with it after.
 */
export const keyEditModifiedDate = (loadedKeys, { rowBefore, rowAfter, keyFields, previousKey, trackingKey }) => {
  const remembered = loadedKeys[previousKey] || {
    key: Object.fromEntries(keyFields.map((field) => [field, rowBefore[field]])),
    modifiedDate: loadedModifiedDate(rowBefore),
  };
  delete loadedKeys[previousKey];
  if (keyFields.every((field) => rowAfter[field] === remembered.key[field])) {
    return remembered.modifiedDate;
  }
  loadedKeys[trackingKey] = remembered;
  return null;
};

/**
 * updatedRows after one cell edit: `field`'s { oldValue, newValue } recorded
 * under `trackingKey`. A key edit gives the row a new tracking key, so what was
 * recorded under `previousKey` moves with it: left behind, a reload would put
 * those edits on the fresh row it was renamed from, and Save would write that
 * row too.
 */
export const trackCellEdit = (changesByKey, { previousKey, trackingKey, field, oldValue, newValue }) => {
  const next = { ...changesByKey };
  let moved;
  if (previousKey !== trackingKey) {
    moved = next[previousKey];
    delete next[previousKey];
  }
  next[trackingKey] = { ...moved, ...next[trackingKey], [field]: { oldValue, newValue } };
  return next;
};

/**
 * updatedRows with an upload's tracked changes added field by field. Replacing
 * a row's entry would drop the edits already tracked for it (and a key edit's
 * marker, which keeps a renamed row through a reload).
 */
export const addTrackedChanges = (changesByKey, entries) => {
  const next = { ...changesByKey };
  Object.keys(entries).forEach((trackingKey) => {
    next[trackingKey] = { ...next[trackingKey], ...entries[trackingKey] };
  });
  return next;
};

/** A saved row takes the writer's new stamp; with no stamp (nothing written) it is unchanged. */
export const adoptModifiedDate = (row, modifiedDate) =>
  modifiedDate ? { ...row, [MODIFIED_DATE_FIELD]: modifiedDate } : row;

// Fields the writer stamps itself; a tracked local "change" to them is never applied.
const WRITER_STAMPED = [MODIFIED_DATE_FIELD, 'MODIFIED_BY'];

/**
 * Put unsaved edits back onto freshly reloaded rows.
 *
 * `changesByKey` is the grid's updatedRows (tracking key -> field -> newValue);
 * `currentRows` are the rows on screen before the reload; `keyFields` are the
 * table's writer key. Rows with a TEMP_ROW_ID are left to the caller.
 *
 * - An edited row: its changed fields go over the fresh row.
 * - A row whose key was edited is a different row now, so the whole local row
 *   is kept. It replaces the fresh row with the same key, if there is one that
 *   no other row on screen already stands for, and otherwise goes at the top.
 *   The row it was renamed from shows fresh. A key edited onto a row already on
 *   screen leaves both rows, each with its own edits, for Save to flag as a
 *   duplicate key; replacing would hide that row and drop its edits. The one
 *   exception is a row edited back onto its own key, which replaces the
 *   unchanged copy of itself a refresh showed meanwhile.
 * - adoptServerModifiedDates (after a 409): each kept row takes the fresh
 *   row's MODIFIED_DATE (a key-edited row with no fresh row sends null), and
 *   saving again is the user's deliberate re-apply. Otherwise (the background
 *   refresh) each row keeps the MODIFIED_DATE its edits were based on.
 * - Tracked MODIFIED_DATE / MODIFIED_BY "changes" are ignored: the writer
 *   stamps both.
 */
export const mergeUnsavedEdits = ({ freshRows, currentRows, changesByKey, trackingKeyOf, keyFields, adoptServerModifiedDates }) => {
  const sameKey = (a, b) => keyFields.every((field) => a[field] === b[field]);
  const baseRowFor = (trackingKey) => currentRows.find((row) => !row.TEMP_ROW_ID && trackingKeyOf(row) === trackingKey);
  const keyEditedKeys = new Set(
    Object.keys(changesByKey).filter((trackingKey) => Object.keys(changesByKey[trackingKey] || {}).some((field) => keyFields.includes(field)))
  );
  const keyEditedRows = [...keyEditedKeys].map(baseRowFor).filter(Boolean);
  // Tracking keys of the loaded rows on screen. A fresh row under one of them
  // is already shown by that row, so a key-edited row must not replace it,
  // unless that row is unchanged and the key-edited row is back on its own key
  // (it carries a MODIFIED_DATE again): then the shown row is only the copy a
  // refresh put up while the key was elsewhere.
  const shownKeys = new Set(currentRows.filter((row) => !row.TEMP_ROW_ID).map(trackingKeyOf));
  const mayReplace = (localRow, freshRow) => {
    const key = trackingKeyOf(freshRow);
    return !shownKeys.has(key) || (!changesByKey[key] && loadedModifiedDate(localRow) !== null);
  };
  const placed = new Set();

  const merged = freshRows.map((freshRow) => {
    const localRow = keyEditedRows.find((row) => !placed.has(row) && sameKey(row, freshRow) && mayReplace(row, freshRow));
    if (localRow) {
      placed.add(localRow);
      return adoptServerModifiedDates
        ? withBaselineOf({ ...localRow, [MODIFIED_DATE_FIELD]: loadedModifiedDate(freshRow) }, freshRow)
        : localRow;
    }
    const trackingKey = trackingKeyOf(freshRow);
    const changes = changesByKey[trackingKey];
    if (!changes || keyEditedKeys.has(trackingKey)) return freshRow;
    const editedRow = adoptServerModifiedDates ? { ...freshRow } : keepBaseModifiedDate(freshRow, baseRowFor(trackingKey));
    Object.keys(changes).filter((field) => !WRITER_STAMPED.includes(field)).forEach((field) => {
      editedRow[field] = changes[field].newValue;
    });
    return editedRow;
  });

  const unplaced = keyEditedRows
    .filter((row) => !placed.has(row))
    .map((row) => (adoptServerModifiedDates ? asNewKey(row) : row));
  return [...unplaced, ...merged];
};
